import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Vault, ArrowRight, Lock } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="theme-admin min-h-screen bg-background text-foreground relative overflow-hidden flex items-center justify-center px-4">
      <div className="absolute inset-0 dot-grid opacity-60" aria-hidden />
      <div className="absolute inset-0 mesh-glow" aria-hidden />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="flex items-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Vault size={22} weight="duotone" className="text-primary" />
          </div>
          <div>
            <div className="font-display text-lg font-bold leading-none">Business Line OC</div>
            <div className="overline text-muted-foreground mt-1">Origination Console</div>
          </div>
        </div>

        <div className="rounded-[var(--radius)] border border-border bg-card card-inner-glow p-8">
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground text-sm mt-2">Sign in to manage credit deals.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="space-y-2">
              <Label className="overline text-muted-foreground">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-secondary border-border h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="overline text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-secondary border-border h-11"
                required
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 font-semibold group"
            >
              {loading ? "Signing in..." : "Sign in"}
              <ArrowRight size={18} className="ml-1 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-border flex items-start gap-2 text-xs text-muted-foreground">
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>Protected access. Contact your administrator if you need an account.</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
